import type { ReactNode } from "react";

/**
 * Glyphs drawn in `currentColor`, so each use site tints them via text color.
 * Internal to the shared primitives; not exported from the barrel.
 */

interface IconProps {
  /** Rendered square size in px (the viewBox scales). */
  size: number;
  className?: string | undefined;
}

const strokeProps = {
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

export function XIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 17.9948 17.9948"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path d="M13.4961 4.4987L4.4987 13.4961" strokeWidth={1.49957} {...strokeProps} />
      <path d="M4.4987 4.4987L13.4961 13.4961" strokeWidth={1.49957} {...strokeProps} />
    </svg>
  );
}

export function CheckIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 13.9931 13.9931"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M11.6609 3.49828L5.24741 9.91178L2.33218 6.99655"
        strokeWidth={1.74914}
        {...strokeProps}
      />
    </svg>
  );
}

export function CheckCircleIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 17.9948 17.9948"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M16.346 7.49783C16.6884 9.17832 16.4444 10.9254 15.6546 12.4477C14.8648 13.9701 13.577 15.1757 12.0059 15.8634C10.4348 16.5512 8.67546 16.6795 7.0212 16.2271C5.36693 15.7747 3.91776 14.7688 2.91536 13.3772C1.91297 11.9856 1.41793 10.2925 1.51281 8.58006C1.60769 6.86767 2.28675 5.23955 3.43675 3.96723C4.58674 2.69491 6.13816 1.85529 7.83229 1.58838C9.52641 1.32148 11.2608 1.64342 12.7463 2.50053"
        strokeWidth={1.49957}
        {...strokeProps}
      />
      <path
        d="M6.74805 8.24762L8.9974 10.497L16.4952 2.99913"
        strokeWidth={1.49957}
        {...strokeProps}
      />
    </svg>
  );
}

export function ClockIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 17.9948 17.9948"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M8.9974 16.4952C13.1383 16.4952 16.4952 13.1383 16.4952 8.9974C16.4952 4.85646 13.1383 1.49957 8.9974 1.49957C4.85646 1.49957 1.49957 4.85646 1.49957 8.9974C1.49957 13.1383 4.85646 16.4952 8.9974 16.4952Z"
        strokeWidth={1.49957}
        {...strokeProps}
      />
      <path d="M8.9974 4.4987V8.9974L11.9965 10.497" strokeWidth={1.49957} {...strokeProps} />
    </svg>
  );
}

export function ChevronDownIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 14 16"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M13.8094 5.22813L13.1906 4.60938C13.0437 4.4625 12.8062 4.4625 12.6594 4.60938L7 10.2563L1.34063 4.60938C1.19375 4.4625 0.95625 4.4625 0.809375 4.60938L0.190625 5.22813C0.04375 5.375 0.04375 5.6125 0.190625 5.75938L6.73438 12.3031C6.88125 12.45 7.11875 12.45 7.26563 12.3031L13.8094 5.75938C13.9562 5.6125 13.9562 5.375 13.8094 5.22813Z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Small chevron pointing right; rotate it via className to point down. */
export function ChevronSmallIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12.9948 12.9948"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M4.87305 9.74637L8.12175 6.49767L4.87305 3.24897"
        strokeWidth={1.0829}
        {...strokeProps}
      />
    </svg>
  );
}

export function InfoCircleIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 17.9948 17.9948"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M8.9974 16.4952C13.1383 16.4952 16.4952 13.1383 16.4952 8.9974C16.4952 4.85646 13.1383 1.49957 8.9974 1.49957C4.85646 1.49957 1.49957 4.85646 1.49957 8.9974C1.49957 13.1383 4.85646 16.4952 8.9974 16.4952Z"
        strokeWidth={1.49957}
        {...strokeProps}
      />
      <path d="M8.9974 11.9965V8.9974" strokeWidth={1.49957} {...strokeProps} />
      <path d="M8.9974 5.99827H9.0049" strokeWidth={1.49957} {...strokeProps} />
    </svg>
  );
}

export function SparkleIcon({ size, className }: IconProps): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 17.9948 17.9948"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M7.4506 11.6216C7.38366 11.3622 7.24841 11.1254 7.05892 10.9359C6.86944 10.7464 6.63264 10.6111 6.37316 10.5442L1.77324 9.35805C1.69476 9.33577 1.62569 9.2885 1.5765 9.22342C1.52732 9.15833 1.50071 9.07898 1.50071 8.9974C1.50071 8.91582 1.52732 8.83647 1.5765 8.77138C1.62569 8.7063 1.69476 8.65903 1.77324 8.63675L6.37316 7.44985C6.63255 7.38297 6.86928 7.24784 7.05876 7.05849C7.24824 6.86914 7.38354 6.6325 7.4506 6.37316L8.63675 1.77324C8.6588 1.69445 8.70602 1.62504 8.77121 1.57559C8.83639 1.52614 8.91596 1.49938 8.99777 1.49938C9.07959 1.49938 9.15916 1.52614 9.22434 1.57559C9.28953 1.62504 9.33675 1.69445 9.3588 1.77324L10.5442 6.37316C10.6111 6.63264 10.7464 6.86944 10.9359 7.05892C11.1254 7.24841 11.3622 7.38366 11.6216 7.4506L16.2216 8.636C16.3007 8.65782 16.3704 8.70499 16.4201 8.77027C16.4698 8.83555 16.4968 8.91534 16.4968 8.9974C16.4968 9.07946 16.4698 9.15924 16.4201 9.22453C16.3704 9.28981 16.3007 9.33698 16.2216 9.3588L11.6216 10.5442C11.3622 10.6111 11.1254 10.7464 10.9359 10.9359C10.7464 11.1254 10.6111 11.3622 10.5442 11.6216L9.35805 16.2216C9.336 16.3004 9.28878 16.3698 9.22359 16.4192C9.15841 16.4687 9.07884 16.4954 8.99703 16.4954C8.91521 16.4954 8.83564 16.4687 8.77046 16.4192C8.70527 16.3698 8.65805 16.3004 8.636 16.2216L7.4506 11.6216Z"
        strokeWidth={1.49957}
        {...strokeProps}
      />
      <path d="M14.9957 2.24935V5.24848" strokeWidth={1.49957} {...strokeProps} />
      <path d="M16.4952 3.74892H13.4961" strokeWidth={1.49957} {...strokeProps} />
      <path d="M2.99913 12.7463V14.2459" strokeWidth={1.49957} {...strokeProps} />
      <path d="M3.74892 13.4961H2.24935" strokeWidth={1.49957} {...strokeProps} />
    </svg>
  );
}
